import type { ComponentProps, ReactNode } from "react";
import { AlertCircle, CreditCard, Info, LockKeyhole } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { formatBillingDate } from "../format";
import type { BillingContext } from "../types";

/** The app's warning token, used by both amber-weight billing notices. */
const WARNING_NOTICE = "border-warning/50 bg-warning/10 text-foreground";

function BillingNotice({
  icon,
  title,
  message,
  actionLabel,
  actionPath,
  variant,
  className,
}: {
  icon: ReactNode;
  title: string;
  message: string;
  actionLabel?: string;
  actionPath?: string;
  variant?: ComponentProps<typeof Alert>["variant"];
  className?: string;
}) {
  const navigate = useNavigate();
  return (
    <Alert variant={variant} className={className}>
      {icon}
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
        <p>{message}</p>
        {actionLabel && actionPath && (
          <Button variant="outline" size="sm" onClick={() => navigate(actionPath)}>
            {actionLabel}
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

/**
 * The pilot notice.
 *
 * `pilot_until` is nullable and every migrated organisation is backfilled
 * open-ended, so gating this on a date would leave the whole existing customer
 * base on a pilot nothing on screen mentions.
 */
function PilotNotice({ pilotUntil }: { pilotUntil: string | null }) {
  const { t } = useLanguage();
  const copy = t.billing.banners;
  return (
    <BillingNotice
      icon={<Info className="h-4 w-4" />}
      title={copy.pilotTitle}
      message={
        pilotUntil
          ? fill(copy.pilotBody, { date: formatBillingDate(pilotUntil) })
          : copy.pilotBodyOpenEnded
      }
    />
  );
}

function ReadOnlyNotice() {
  const copy = useLanguage().t.billing.banners;
  return (
    <BillingNotice
      variant="destructive"
      icon={<LockKeyhole className="h-4 w-4" />}
      title={copy.readOnlyTitle}
      message={copy.readOnlyBody}
      actionLabel={copy.viewInvoice}
      actionPath="/billing/invoices"
    />
  );
}

/**
 * An invoice that is past due, or one whose automatic charge was declined.
 *
 * The overdue signal covers both, and the automatic half has NO due date —
 * Stripe only fills `due_date` on `send_invoice` invoices — so the body has a
 * second wording that names the same 30 day consequence without a deadline it
 * cannot state.
 */
function OverdueNotice({ invoice }: { invoice: NonNullable<BillingContext["overdue_invoice"]> }) {
  const copy = useLanguage().t.billing.banners;
  return (
    <BillingNotice
      className={WARNING_NOTICE}
      icon={<AlertCircle className="h-4 w-4" />}
      title={fill(copy.overdueTitle, { number: invoice.invoice_number })}
      message={
        invoice.due_on
          ? fill(copy.overdueBody, { date: formatBillingDate(invoice.due_on) })
          : copy.overdueBodyNoDueDate
      }
      actionLabel={copy.viewInvoice}
      actionPath="/billing/invoices"
    />
  );
}

function DetailsMissingNotice() {
  const copy = useLanguage().t.billing.banners;
  return (
    <BillingNotice
      className={WARNING_NOTICE}
      icon={<CreditCard className="h-4 w-4" />}
      title={copy.detailsMissingTitle}
      message={copy.detailsMissingBody}
      actionLabel={copy.detailsMissingAction}
      actionPath="/billing/details"
    />
  );
}

/**
 * Card or SEPA is the chosen method, but Stripe holds nothing to charge.
 *
 * Same standing as details-missing — no bill-increasing action until it is
 * fixed — so it gets the same weight and an action link to the one page where
 * the Checkout setup can be started.
 */
function PaymentMethodMissingNotice() {
  const copy = useLanguage().t.billing.banners;
  return (
    <BillingNotice
      className={WARNING_NOTICE}
      icon={<CreditCard className="h-4 w-4" />}
      title={copy.paymentMethodMissingTitle}
      message={copy.paymentMethodMissingBody}
      actionLabel={copy.setUpPaymentMethod}
      actionPath="/billing/details"
    />
  );
}

/**
 * An automatic charge Stripe could not take.
 *
 * An invoice collected automatically carries no due date — Stripe leaves
 * `due_date` null outside `send_invoice` — so the failure date, not a missed
 * deadline, is what this banner can name. The read-only clock still runs from
 * the invoice date, which is why the body says so.
 */
function PaymentFailedNotice({
  invoice,
}: {
  invoice: NonNullable<BillingContext["failed_payment_invoice"]>;
}) {
  const copy = useLanguage().t.billing.banners;
  return (
    <BillingNotice
      className={WARNING_NOTICE}
      icon={<AlertCircle className="h-4 w-4" />}
      title={fill(copy.paymentFailedTitle, { number: invoice.invoice_number })}
      message={fill(copy.paymentFailedBody, { date: formatBillingDate(invoice.failed_on) })}
      actionLabel={copy.updatePaymentMethod}
      actionPath="/billing/details"
    />
  );
}

/**
 * Every billing state that applies right now, stacked.
 *
 * Deliberately a list of independent conditions rather than a switch: pilot and
 * details-missing are both true for a fresh organisation, and a failed card
 * charge can coexist with a missing payment method.
 */
export function BillingBanners({ context }: { context: BillingContext }) {
  const { t } = useLanguage();
  return (
    <div className="space-y-3" aria-label={t.billing.nav.group}>
      {context.is_pilot && context.allows_legacy_building_creation !== false && (
        <PilotNotice pilotUntil={context.pilot_until} />
      )}
      {context.is_read_only && <ReadOnlyNotice />}
      {context.overdue_invoice && <OverdueNotice invoice={context.overdue_invoice} />}
      {context.failed_payment_invoice && (
        <PaymentFailedNotice invoice={context.failed_payment_invoice} />
      )}
      {context.details_missing && <DetailsMissingNotice />}
      {context.payment_method_missing && <PaymentMethodMissingNotice />}
    </div>
  );
}
